import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getOrgMember } from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.actions'
import { getOrgOwnersAndAdmins } from 'src/app/PeopleOwnersAndAdmins/redux/peopleOwnersAndAdmins.actions'
import { selectPeopleOwnersAndAdmins } from 'src/app/PeopleOwnersAndAdmins/redux/peopleOwnersAndAdmins.selectors'
import Box from 'src/components/Box/Box'
import CardApprovalsBoxContent from 'src/components/CardApprovalsBox/CardApprovalsBoxContent'
import CardApprovalsForm from 'src/components/CardApprovalsBox/CardApprovalsForm'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  ApprovalRuleResponse,
  OrganizationsListModel,
} from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { selectMembersListModelsFromState } from 'src/redux/selectors/data/members.selectors'
import { css } from 'styled-components'

interface CardApprovalsBoxProps {
  user?: UserListModel
  organization: OrganizationsListModel
  cardApprovalRules?: ApprovalRuleResponse
  cardApprovalsEnabled: boolean
}

const CardApprovalsBox: React.FC<CardApprovalsBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()
  const ownersAndAdminsFullPaginatedData = useSelector(
    selectPeopleOwnersAndAdmins,
  )
  const users = useSelector(selectMembersListModelsFromState)
  const [isCardApprovalRuleBeingEdited, setIsCardApprovalRuleBeingEdited] =
    useState<boolean>(false)

  const { user, organization, cardApprovalRules } = props

  const approver =
    cardApprovalRules?.approverId !== undefined
      ? users[cardApprovalRules.approverId]
      : undefined

  const fetchOrgOwnersAndAdmins = (
    filterInput?: string,
    page?: number,
  ): void => {
    dispatch(
      getOrgOwnersAndAdmins(organization.id, {
        page: page || 0,
        count: 10,
        search: filterInput,
        renderMetrics: false,
      }),
    )
  }
  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchOrgOwnersAndAdmins,
    ...ownersAndAdminsFullPaginatedData,
  }

  useEffect(() => {
    if (cardApprovalRules?.approverId !== undefined) {
      // to load current approver (required to avoid user id flashing in place of the user's name/email)
      dispatch(getOrgMember(cardApprovalRules?.approverId))
    }
  }, [cardApprovalRules?.approverId])

  return (
    <Box
      className={'flex-column'}
      css={css`
        height: fit-content;
      `}
      noShadow
      outlineBorder
    >
      <div
        css={css`
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 10px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Card Approvals')}
          </span>
        </div>
        {user?.hasOrgAdminPermissions && !isCardApprovalRuleBeingEdited && (
          <HoverableLink
            onClick={(): void => {
              setIsCardApprovalRuleBeingEdited(true)
            }}
            ariaLabelSource={'Update'}
            css={css`
              display: flex;
              align-items: center;
            `}
            testId={'updateCardApprovalsBtn'}
          >
            {translate('Update')}
            <SvgUpdate
              css={css`
                transform: scale(0.7);
                margin-left: 4px;
              `}
            />
          </HoverableLink>
        )}
      </div>
      {isCardApprovalRuleBeingEdited && user !== undefined ? (
        <CardApprovalsForm
          userInfiniteScrollProps={userInfiniteScrollProps}
          closeCardApprovalsForm={(): void => {
            setIsCardApprovalRuleBeingEdited(false)
          }}
          organization={organization}
          approver={approver}
          cardApprovalsEnabled={props.cardApprovalsEnabled}
          cardApprovalRules={props.cardApprovalRules}
        />
      ) : (
        <CardApprovalsBoxContent
          organization={organization}
          approver={approver}
          cardApprovalRules={props.cardApprovalRules}
          cardApprovalsEnabled={props.cardApprovalsEnabled}
        />
      )}
    </Box>
  )
}

export default CardApprovalsBox
